<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Col, Input, Label, Nav, Row, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { Tab } from '@client/pages/brandkit/components';

  import { template } from '@client/stores/virtual-background';

  import { getContrastColor } from '@client/utils/color';

  import BackgroundImages from './background-images.svelte';
  import BlurIcon from './blur-icon.svelte';
  import Gradient from './gradient.svelte';
  import StockImages from './stock-images.svelte';

  /** @typedef {'color' | 'image' | 'stock'} BackgroundType */

  /** @type {string} */
  export let background;
  /** @type {string} */
  export let foreground = '#FFFFFF';
  /** @type {(bg: string, fg: string) => void} */
  export let onChange = () => {};

  /**
   * @param {string} bg
   * @returns {BackgroundType}
   */
  const getInitialBackgroundType = (bg) => {
    if (bg?.startsWith('https://smart.')) return 'image';
    if (bg?.startsWith('https://')) return 'stock';
    return 'color';
  };

  /** @type {BackgroundType} */
  let backgroundType = getInitialBackgroundType(background);

  let lastBackground = background;
  let lastForeground = foreground;

  $: if (background !== lastBackground || foreground !== lastForeground) {
    onChange(background, foreground);
    lastBackground = background;
    lastForeground = foreground;
  }

  onMount(() => {
    backgroundType = getInitialBackgroundType(background);
  });
</script>

<div class="layout">
  <header class="sticky-top bg-white">
    <div class="p-3 border-bottom">
      <Row class="align-items-center">
        <Label for="bg_blur" xs={1} id="bg_sharp">
          <Icon name="image" />
          <span class="visually-hidden">{$format('label.BACKGROUND_SHARP')}</span>
        </Label>
        <Tooltip target="bg_sharp" placement="bottom">{$format('label.BACKGROUND_SHARP')}</Tooltip>
        <Col>
          <Input id="bg_blur" type="range" min="0" max="20" step="1" bind:value={$template.blur} class="flex-grow-1" />
        </Col>
        <Label for="bg_blur" xs={1} id="bg_blurred">
          <BlurIcon />
          <span class="visually-hidden">{$format('label.BACKGROUND_BLURRED')}</span>
        </Label>
        <Tooltip target="bg_blurred" placement="bottom">{$format('label.BACKGROUND_BLURRED')}</Tooltip>
      </Row>

      <Row class="align-items-center">
        <Label for="bg_dark" xs={1} id="bg_dark">
          <Icon name="moon" />
          <span class="visually-hidden">{$format('label.BACKGROUND_DARK')}</span>
        </Label>
        <Tooltip target="bg_dark" placement="bottom">{$format('label.BACKGROUND_DARK')}</Tooltip>
        <Col>
          <Input
            id="bg_dark"
            type="range"
            min=".2"
            max="1"
            step="0.1"
            bind:value={$template.brightness}
            class="flex-grow-1"
          />
        </Col>
        <Label for="bg_dark" xs={1} id="bg_bright">
          <Icon name="sun" />
          <span class="visually-hidden">{$format('label.BACKGROUND_BRIGHT')}</span>
        </Label>
        <Tooltip target="bg_bright" placement="bottom">{$format('label.BACKGROUND_BRIGHT')}</Tooltip>
      </Row>
    </div>

    <Nav pills>
      <Tab size="sm" active={backgroundType === 'color'} icon="palette" on:click={() => (backgroundType = 'color')}>
        {$format('label.BACKGROUND_COLOR')}
      </Tab>
      <Tab size="sm" active={backgroundType === 'image'} icon="image" on:click={() => (backgroundType = 'image')}>
        {$format('label.BACKGROUND_IMAGES')}
      </Tab>
      <Tab size="sm" active={backgroundType === 'stock'} icon="camera" on:click={() => (backgroundType = 'stock')}>
        {$format('label.BACKGROUND_STOCK_IMAGES')}
      </Tab>
    </Nav>
  </header>

  <div class="content-area">
    {#if backgroundType === 'color'}
      <Gradient
        value={background}
        onChange={(gradient) => {
          background = gradient;
          if (CSS.supports('color', gradient)) {
            foreground = getContrastColor(gradient);
          }
        }}
      />
    {:else if backgroundType === 'image'}
      <BackgroundImages onSelect={(val) => (background = val)} />
    {:else if backgroundType === 'stock'}
      <StockImages
        active={backgroundType === 'stock'}
        onSelect={(img, avg) => {
          background = img;
          foreground = getContrastColor(avg);
        }}
      />
    {/if}
  </div>
</div>

<style lang="scss">
  .layout {
    display: grid;
    grid-template-rows: auto auto 1fr;
    height: 100%;
  }

  .content-area {
    overflow-y: auto;
  }
</style>
